(ns shared.core.components.search
  (:require [shared.core.reactive :as re]
            [shared.core.style :as style]
            ["fuse.js" :as Fuse]
            [shared.core.components.input :as input]
            [shared.core.components.pill :as pill]
            [shared.core.components.tree :as tree]
            [shared.core.util :as util]
            [clj-fuzzy.metrics :as fuz]
            [shared.core.components.interactive :as interactive]
            [shared.core.components.theme :as theme]
            [clojure.data :as d]
            [clojure.string :as s]
            [goog.dom :as gdom]
            [goog.events :as gevents]))

(def image-size 50)

(defn detail-box [{:keys [style]} & contents]
  (into [theme/theme
         :div {:style (merge {:background-color (util/hex->rgba (style/color :green :base)
                                                                0.1)
                              :color (style/color :green :base)
                              :font-weight style/font-medium-weight
                              :display :flex
                              :align-items :center
                              :justify-content :center
                              :flex "0 0 auto"
                              :padding "0 10px"
                              :height image-size}
                             style)
               :theme (style/dark-style {:background-color (style/dark-color :background :lighter-1)
                                         :color (style/dark-color :on-background :base)})}]
        contents))

(defn loading-bar [{:keys [height width style]
                    :or {height 10
                         width "80%"}}]
  [theme/theme
   :div {:style (merge {:background-color (style/color :gray :shade-7)
                        :border-radius 2
                        :height height
                        :width width}
                       style)
         :theme (style/dark-style {:background-color (style/dark-color :on-surface :base)})}])

(defn loader-bar-container [{:keys [border? images?]} & contents]
  [:div {:style (cond-> {:cursor :default
                         :display :flex
                         :align-items :center
                         :padding 15
                         :pointer-events :none
                         :width "100%"}
                        border? (assoc :border-bottom (str "1px solid " (style/color :gray :shade-7))))}
   (when images?
     [loading-bar {:height image-size
                   :width image-size
                   :style {:margin-right 8}}])
   (into [:div {:style {:display :flex
                        :flex-direction :column
                        :flex 1}}]
         contents)])

(defn loading-placeholder [{:keys [images?]}]
  [:div {:style {:display :flex
                 :flex-direction :column
                 :width "100%"}}
   [loader-bar-container {:border? true
                          :images? images?}
    [loading-bar {:height 15
                  :width "40%"}]
    [loading-bar {:width "70%"
                  :style {:margin-top 6}}]]
   [loader-bar-container {:images? images?}
    [loading-bar {:height 15
                  :width "33%"}]
    [loading-bar {:style {:margin-top 6}}]]])

(defn pills-container [{:keys [style pills]}]
  (into [:div {:style (merge {:display :flex
                              :flex-wrap :wrap
                              :margin-top 3}
                             style)}]
        (map (fn [p]
               [pill/light-gray-pill {:style {:margin "0 2px 2px 0"}} p])
             pills)))

(defn click-to-copy-pills-container [{:keys [style pills]}]
  (into [:div {:style (merge {:display :flex
                              :flex-wrap :wrap
                              :margin-top 3}
                             style)}]
        (->> pills
             (filter identity)
             (map (fn [p]
                    [pill/click-to-copy-pill (assoc p :label-style {:margin "0 2px 2px 0"})])))))

(defn build-results [{:keys [data score? match-scores score-threshold]}]
  (let [match-ids (into #{} (keys match-scores))]
    (some->> data
             (mapv (fn [{:keys [id title image description price pills pills-container-style right-end-content] :as o}]
                     (when (or (and (identity o) (contains? match-ids id)) (false? score?))
                       (let [pos-price? (pos? price)
                             price (when price (str price))]
                         (assoc (dissoc o :price)
                           :title [:div {:style {:display :flex
                                                 :align-items :center}}
                                   (when image
                                     [:div {:style {:background-image (str "url(" image ")")
                                                    :background-size :cover
                                                    :background-position "50% 50%"
                                                    :flex (str "0 0 " image-size "px")
                                                    :margin-right 8
                                                    :height image-size}}])
                                   [:div {:style {:display :flex
                                                  :flex-direction :column
                                                  :width "100%"}}
                                    (into [:div {:style {:color (style/color :gray :base)
                                                         :font-size 14
                                                         :font-weight style/font-bold-weight}}]
                                          [title])
                                    (when description
                                      (into [:div {:style {:color (style/color :gray :base)
                                                           :font-size 13
                                                           :line-height 1.3
                                                           :margin-top 2}}]
                                            [description]))
                                    (when pills
                                      [pills-container {:style pills-container-style
                                                        :pills pills}])]
                                   (when pos-price?
                                     [detail-box {:style {:margin-left 5}}
                                      (util/pennies->readable-price price)])
                                   (when right-end-content
                                     (into [:div {:style {:flex "0 0 auto"}}]
                                           right-end-content))]
                           :score (if (false? score?) 0 (get match-scores id)))))))
             (filterv #(< (or (:score %) 1) score-threshold))
             (sort-by :score))))

(defn add-category-prefix-to-ids [data]
  (if (map? data)
    (->> data
         (mapv (fn [[k v]]
                 [k (assoc v :data (map #(assoc % :id (str (name k) "-" (:id %))) (:data v)))]))
         (into {}))
    data))

(defn string-or-all-strings-in-coll [v]
  (if (string? v) v (s/join " " (flatten (util/find-all-of-type v string?)))))

(defn build-fuse-list [list]
  (let [list (add-category-prefix-to-ids list)]
    (some->> (if (map? list)
               (map second list)
               list)
             (map (fn [{:keys [data id content title description tags]}]
                    (if data
                      (build-fuse-list data)
                      {:id id
                       :title (string-or-all-strings-in-coll (or title content))
                       :description (string-or-all-strings-in-coll description)
                       :tags (->> tags
                                  (filter identity)
                                  (map str))})))
             (flatten)
             (filter identity)
             (mapv util/->camelCaseString))))

(defn find-all-data-ids [data]
  (mapv :id
        (if (map? data)
          (flatten (map #(-> % (second) :data) data))
          data)))

(defn category [{:keys [title style size loading? total-results results]}]
  (into [theme/theme
         :div {:style (merge {:background-color (style/color :gray :shade-7)
                              :font-size 14
                              :font-weight style/font-bold-weight
                              :letter-spacing "0.8px"
                              :text-transform :uppercase
                              :display :flex
                              :justify-content :space-between
                              :padding (condp = size
                                         :large "9px 10px"
                                         "7px 8px")}
                             style)
               :theme (style/dark-style {:background-color (style/dark-color :surface :lighter-1)
                                         :color (style/dark-color :on-surface :base)})}]
        (concat [title]
                (when-not loading?
                  [[theme/theme
                    :span {:style {:letter-spacing :normal
                                   :text-transform :none
                                   :flex "0 0 auto"
                                   :margin-left 5}
                           :theme (style/dark-style {:color (style/dark-color :on-surface :base)})}
                    (str (count results) " of " (some-> total-results (util/readable-number)))]]))))

(defn result [{:keys [result-transf] :as res}]
  (let [{:keys [id title active? disabled? set-active style size last? select-by-id]} (if result-transf
                                                                                        (result-transf res)
                                                                                        res)]
    (let [active? (and active? (not disabled?))]
      (into [theme/theme interactive/interactive
             :div {:key id
                   :class-name (->> (cond-> ["novo-search-result"]
                                            active? (conj "active"))
                                    (s/join " "))
                   :style (merge {:border-bottom (when-not last? (str "1px solid " (style/color :gray :shade-7)))
                                  :color (style/color :gray :base)
                                  :cursor :pointer
                                  :font-size 14
                                  :padding (condp = size
                                             :large "9px 10px"
                                             "7px 8px")
                                  :transition "0.2s"}
                                 (when disabled?
                                   {:cursor :not-allowed
                                    :opacity 0.6})
                                 (when active?
                                   {:background-color (util/hex->rgba (style/color :green :base) 0.1)})
                                 style)
                   :theme (style/dark-style
                            (merge {:background-color (style/dark-color :surface :base)
                                    :color (style/dark-color :on-background :base)
                                    :border-bottom (when-not last?
                                                     (str "1px solid " (style/dark-color :surface :lighter-1)))}
                                   (when active?
                                     {:background-color (style/dark-color :primary :base)
                                      :color (style/dark-color :on-primary :base)})))
                   :on-click (when-not disabled? #(select-by-id id))
                   :on-mouse-over (when-not disabled? #(set-active id :hover))}]
            [title]))))

(defn category-results [{:keys [active-id results loading? loading-placeholder-config set-active-result size
                                select-result-by-id]
                         :as cat}]
  (into [:div {:style {:display :flex
                       :flex-direction :column
                       :width "100%"}}
         [category (assoc cat :size size)]]
        (if loading?
          [[loading-placeholder loading-placeholder-config]]
          (map-indexed (fn [idx res]
                         [result (assoc res :size size
                                            :select-by-id select-result-by-id
                                            :active? (= (:id res) active-id)
                                            :set-active set-active-result
                                            :last? (= idx (dec (count results))))])
                       results))))

(defn no-results [{:keys [style]} & contents]
  (into [theme/theme
         :div {:style (merge {:color (style/color :gray :base)
                              :font-size 14
                              :font-weight style/font-medium-weight
                              :text-align :center
                              :padding 12}
                             style)
               :theme (style/dark-style {:color (style/dark-color :on-background :base)})}]
        contents))

(defn search [{:keys [val-subscribe form-path form-name open?]}]
  (let [input-id (gensym "search-input-")
        val- (if val-subscribe
               (re/subscribe val-subscribe)
               (when (and form-path form-name)
                 (re/subscribe [:form/val- form-path form-name])))
        cur-data* (re/ratom nil)
        fuse-inst* (re/ratom nil)
        active-id* (re/ratom nil)
        active-id-method* (re/ratom nil)
        delayed-blurred?* (re/ratom (not open?))]
    (re/create-class
      {:component-did-mount
       (fn [this]
         (let [{:keys [data fuse-list fuse-config]} (re/props this)
               fuse-list (if (or (not data) (= data :loading)) [] (or fuse-list (build-fuse-list data)))]
           (reset! cur-data* data)
           (reset! fuse-inst* (new Fuse
                                   (clj->js fuse-list)
                                   (clj->js (util/->camelCaseString (merge {:keys [:title :description :tags]
                                                                            :include-score true}
                                                                           fuse-config)))))))
       :component-did-update
       (fn [this]
         (let [{:keys [data fuse-list]} (re/props this)
               fuse-list (if (or (not data) (= data :loading)) [] (or fuse-list (build-fuse-list data)))]
           (when (and @fuse-inst* (not= data @cur-data*))
             (let [fuse-list (if (= data :loading) [] (or fuse-list (build-fuse-list data)))]
               (.setCollection ^js @fuse-inst* (clj->js fuse-list))
               (reset! cur-data* data)))))
       :reagent-render
       (fn [{:keys [container-style label required? form-path form-name label-info size data extra-on-search-change
                    score-threshold loading-placeholder-config on-result-select max-results-per-category max-results
                    placeholder auto-focus on-focus on-blur open? floating? input-focused?* score? class-name
                    input-args no-results-text]
             :or {score-threshold 0.6
                  max-results 10}}]
         (let [data (add-category-prefix-to-ids data)
               loading? (= data :loading)
               match-scores (when-not loading?
                              (if (and val- (not-empty @val-))
                                (let [flattened-children (util/flatten-tree {:children (if (map? data)
                                                                                         (->> data
                                                                                              (map (fn [[_ v]]
                                                                                                     (:data v)))
                                                                                              (flatten))
                                                                                         data)})
                                      exact-tag-matches (some->> flattened-children
                                                                 (filter (fn [{:keys [exact-tags]}]
                                                                           (some #(= (str %) @val-)
                                                                                 (filter identity exact-tags))))
                                                                 (map :id)
                                                                 (map (fn [id] [id 0]))
                                                                 (into {}))]
                                  (if (pos? (count exact-tag-matches))
                                    exact-tag-matches
                                    (let [val-words (some-> @val- (s/split #" "))
                                          substring-tag-matches (when (or (and (some-> val-words
                                                                                       (count)
                                                                                       (>= 2))
                                                                               (some-> val-words
                                                                                       (last)
                                                                                       (count)
                                                                                       (> 2)))
                                                                          (some-> val-words
                                                                                  (count)
                                                                                  (>= 3)))
                                                                  (some->> flattened-children
                                                                           (filter (fn [{:keys [tags]}]
                                                                                     (when (not-empty tags)
                                                                                       (some #(s/includes?
                                                                                                (s/lower-case (str %))
                                                                                                (s/lower-case @val-))
                                                                                             (filter identity tags)))))
                                                                           (map :id)
                                                                           (map (fn [id] [id 0]))
                                                                           (into {})))]
                                      (merge (some->> (when (and @fuse-inst* val-) (.search @fuse-inst* @val-))
                                                      (js->clj)
                                                      (util/->kebab-case-keywords)
                                                      (filter #(< (:score %) score-threshold))
                                                      (map (fn [{:keys [item score]}] [(:id item) score]))
                                                      (into {}))
                                             substring-tag-matches))))
                                (some->> (find-all-data-ids data)
                                         (map (fn [id] [id 0]))
                                         (into {}))))
               max-results-per-category (when (map? data)
                                          (or max-results-per-category (js/Math.floor (/ max-results (count data)))))
               results (cond (or loading?
                                 (not data)
                                 (not val-))
                             []

                             (map? data)
                             (some->> data
                                      (map (fn [[k v]]
                                             (let [all-results (build-results {:data (:data v)
                                                                               :score? (:score? v)
                                                                               :match-scores match-scores
                                                                               :score-threshold score-threshold})]
                                               [k (assoc v :total-results (count all-results)
                                                           :results (cond
                                                                      (:errors? v)
                                                                      nil

                                                                      (:loading? v)
                                                                      [{:title (if (and val- @val-) @val- "")
                                                                        :loading? true
                                                                        :loading-placeholder-config (:loading-placeholder-config v)}]

                                                                      :else
                                                                      (take max-results-per-category all-results)))])))
                                      (filter (fn [[_ v]] (not-empty (:results v))))
                                      (into {}))

                             :else
                             (build-results {:data data
                                             :score? score?
                                             :match-scores match-scores
                                             :score-threshold score-threshold}))
               category? (map? results)
               set-active-result (fn [id method]
                                   (reset! active-id-method* method)
                                   (reset! active-id* id))
               flattened-results (if category?
                                   (->> results
                                        (map (fn [[_ {:keys [results]}]] results))
                                        (flatten))
                                   results)
               selectable-results-by-id (->> flattened-results
                                             (take max-results)
                                             (filter #(not (:disabled? %)))
                                             (map-indexed (fn [idx {:keys [id] :as res}]
                                                            [id (assoc res :idx idx)]))
                                             (into {}))
               selectable-result-ids (->> flattened-results
                                          (filter #(not (:disabled? %)))
                                          (mapv :id))
               clear-active-id #(do
                                  (reset! active-id* nil)
                                  (reset! active-id-method* nil))
               on-result-select (or on-result-select
                                    (when (and form-path form-name)
                                      #(do
                                         (re/dispatch [:form/change form-path form-name (:id %)])
                                         (when-let [input-el (.getElementById js/document input-id)]
                                           (.blur input-el)))))
               select-result-by-id #(on-result-select (get selectable-results-by-id %))]
           [:div {:class-name class-name
                  :style (merge {:display :flex
                                 :flex-direction :column
                                 :position :relative}
                                container-style)}
            [input/input (merge {:id input-id
                                 :label label
                                 :required? required?
                                 :focused?* input-focused?*
                                 :on-focus #(do
                                              (reset! delayed-blurred?* false)
                                              (when on-focus (on-focus %)))
                                 :on-blur #(do
                                             (util/wait 200 (fn [] (reset! delayed-blurred?* true)))
                                             (when on-blur (on-blur %)))
                                 :label-info label-info
                                 :size size
                                 :form-path form-path
                                 :form-name form-name
                                 :placeholder placeholder
                                 :auto-focus auto-focus
                                 :extra-on-change (fn [v]
                                                    (clear-active-id)
                                                    (when extra-on-search-change (extra-on-search-change v)))
                                 :on-key-down (fn [e]
                                                (cond
                                                  (util/up-key-event? e)
                                                  (let [cur-idx (when @active-id*
                                                                  (:idx (get selectable-results-by-id @active-id*)))
                                                        next-idx (if (or (not cur-idx) (= cur-idx 0))
                                                                   (dec (count selectable-result-ids))
                                                                   (dec cur-idx))]
                                                    (reset! active-id-method* :arrow)
                                                    (try
                                                      (reset! active-id* (nth selectable-result-ids next-idx))
                                                      (catch :default _)))

                                                  (util/down-key-event? e)
                                                  (let [cur-idx (when @active-id*
                                                                  (:idx (get selectable-results-by-id @active-id*)))
                                                        next-idx (if (or (not cur-idx)
                                                                         (= cur-idx (dec (count selectable-result-ids))))
                                                                   0
                                                                   (inc cur-idx))]
                                                    (reset! active-id-method* :arrow)
                                                    (try
                                                      (reset! active-id* (nth selectable-result-ids next-idx))
                                                      (catch :default _)))

                                                  (util/enter-key-event? e)
                                                  (when @active-id* (select-result-by-id @active-id*))))}
                                input-args)]
            (when (or open?
                      (and (not-empty results) (not @delayed-blurred?*))
                      (and val- (not-empty @val-) (not @delayed-blurred?*)))
              (into [theme/theme
                     :div {:class-name "novo-search-result-container"
                           :style (cond-> {:background-color (style/color :white :base)
                                           :box-shadow style/hover-box-shadow
                                           :display :flex
                                           :flex-direction :column
                                           :margin-top 5
                                           :width "100%"}
                                          floating? (assoc :position :absolute
                                                           :top (+ (if (= size :large) 45 41)
                                                                   (when label 10))
                                                           :left 0
                                                           :width "100%"
                                                           :z-index (style/z :medium)))
                           :theme (style/dark-style {:background-color (style/dark-color :surface :base)
                                                     :border (str "1px solid " (style/dark-color :surface :lighter-1))})
                           :on-mouse-out #(when (= @active-id-method* :hover) (clear-active-id))}]
                    (cond
                      loading? [[loading-placeholder loading-placeholder-config]]
                      (= (count results) 0) [[no-results {}
                                              (or no-results-text
                                                  (str "No results found"
                                                       (when (and val- (not (s/blank? @val-)))
                                                         (str " for \""
                                                              @val-
                                                              "\""))))]]
                      category? (map (fn [[_ cat]]
                                       [category-results (assoc cat :size size
                                                                    :select-result-by-id select-result-by-id
                                                                    :set-active-result set-active-result
                                                                    :active-id @active-id*)])
                                     results)
                      :else [(into [:div {:style {:display :flex
                                                  :flex-direction :column
                                                  :width "100%"}}]
                                   (concat (map-indexed (fn [idx res]
                                                          [result (assoc res :size size
                                                                             :select-by-id select-result-by-id
                                                                             :active? (= (:id res) @active-id*)
                                                                             :set-active set-active-result
                                                                             :last? (= idx (dec (count results))))])
                                                        (take max-results results))
                                           [(when (> (count results) max-results)
                                              [theme/theme
                                               :div {:style {:background-color (style/color :gray :shade-7)
                                                             :color (style/color :gray :base)
                                                             :font-size 12
                                                             :font-weight style/font-medium-weight
                                                             :padding 5
                                                             :text-align :center}
                                                     :theme (style/dark-style {:background-color (style/dark-color :surface :lighter-1)
                                                                               :color (style/dark-color :on-surface :base)})}
                                               (str "Displaying "
                                                    max-results
                                                    " of "
                                                    (some-> (count results) (util/readable-number))
                                                    " "
                                                    (util/pluralize "Result" (count results)))])]))])))]))})))

(defn all-children-ids [children]
  (let [children-ids (map :id children)
        sub-children (->> children
                          (map :children)
                          (flatten)
                          (filter identity))]
    (into #{} (concat children-ids (when (not-empty sub-children) (all-children-ids sub-children))))))

(defn data-with-all-children-ids [])

(defn- descendant-ids
  "Lazy breadth-first ids of every descendant, so callers that only need to know
   whether *some* descendant matches can stop at the first hit instead of building
   a set of every id in the subtree."
  [children]
  (lazy-seq
    (when (seq children)
      (concat (map :id children)
              (descendant-ids (->> children (mapcat :children) (filter identity)))))))

(defn build-tree-results [{:keys [data match-scores score-threshold set-active active-id ids-to-avoid sort-by-score?
                                  support-arrow-key-selecting? theme] :as args}]
  ;; This recurses once per node, so building a set of every match id here just to
  ;; test emptiness re-hashed thousands of string keys per node
  (let [get-score #(or (get match-scores %) 1)]
    (if (empty? match-scores)
      []
      (->> data
           (map (fn [{:keys [id expandable? expanded? on-check on-click disabled? children ancestor-match?] :as r}]
                  (let [score (get-score id)
                        ;; Short-circuits on the first matching descendant rather than
                        ;; collecting every id in the subtree into a set, per node
                        visible? (or ancestor-match?
                                     (< score score-threshold)
                                     (boolean (some #(< (get-score %) score-threshold)
                                                    (descendant-ids children))))
                        child-ancestor-match? (or ancestor-match?
                                                  (< score score-threshold))
                        render-sub-children? (or (not expandable?) (and visible? expanded?))
                        ;; Hoisted: this is the same set for every child, so building it
                        ;; inside the map below rebuilt it once per sibling
                        sibling-ids (when (and (not-empty children) render-sub-children?)
                                      (into #{} (map :id children)))
                        children-res (when (not-empty children)
                                       (build-tree-results
                                         (assoc args :data (when render-sub-children?
                                                             (map (fn [child]
                                                                    (assoc child :sibling-ids sibling-ids
                                                                                 :ancestor-match? child-ancestor-match?))
                                                                  children)))))
                        active-style {:background-color (condp = theme
                                                          :dark (style/dark-color :primary :lighter-1)
                                                          (util/hex->rgba (style/color :green :base) 0.1))
                                      :color (condp = theme
                                               :dark (style/dark-color :on-primary :base)
                                               (style/color :gray :base))}]
                    (when (or (and expandable? (not-empty children-res)) visible?)
                      (cond-> (assoc r
                                :hover-style? false
                                :bubbled-up-score (min score
                                                       (or (:bubbled-up-score (first children-res)) 1)))

                              (and id (= id active-id))
                              (assoc :content-container-style active-style
                                     :dark-content-container-style active-style)

                              (and expandable? (not-empty children-res))
                              (assoc :children (if ids-to-avoid
                                                 (filter #(not (contains? ids-to-avoid (:id %)))
                                                         children-res)
                                                 children-res))

                              (and support-arrow-key-selecting? (or on-check on-click) (not disabled?))
                              (assoc :on-mouse-over #(set-active id :hover)))))))
           (#(if sort-by-score? (sort-by :bubbled-up-score %) %))
           (filter identity)))))

(defn selectable-tree-results [{:keys [id children disabled? checkable? on-check expandable? expanded?] :as i}
                               {:keys [ids-to-allow] :as args}]
  (when-not disabled?
    (concat (when (or (and ids-to-allow (contains? ids-to-allow id)) (not checkable?) on-check) [i])
            (when (or (not expandable?) expanded?) (flatten (map #(selectable-tree-results % args) children))))))

(defn search-tree [{:keys [val-subscribe form-path form-name]}]
  (let [input-id (gensym "search-tree-input-")
        val- (if val-subscribe
               (re/subscribe val-subscribe)
               (when (and form-path form-name)
                 (re/subscribe [:form/val- form-path form-name])))
        theme- (re/subscribe [:core/theme-])
        dom-node- (atom nil)
        cur-data* (re/ratom nil)
        fuse-inst* (re/ratom nil)
        set-default-value* (re/ratom nil)
        search-shown?* (re/ratom false)
        active-id* (re/ratom nil)
        active-id-method* (re/ratom nil)
        set-active-result (fn [id method]
                            (reset! active-id-method* method)
                            (reset! active-id* id))
        cached-match-scores- (re/ratom {})
        el-mousedown #(when @search-shown?*
                        ;; This prevents the search input from losing focus when clicking a result
                        (util/stop-event %)
                        false)
        set-input-default-value (fn [v]
                                  (when (and v (not= @set-default-value* v) form-path form-name)
                                    (re/dispatch [:form/change form-path form-name v])
                                    (when-let [el (.getElementById js/document input-id)]
                                      (set! (.-value el) v))
                                    (reset! set-default-value* v)))]
    (re/create-class
      {:component-did-mount
       (fn [this]
         (let [{:keys [data fuse-list fuse-config search-default-value]} (re/props this)
               fuse-list (when data (if (= data :loading) [] (or fuse-list (build-fuse-list (util/flatten-tree
                                                                                              {:children data})))))]
           (reset! dom-node- (re/dom-node this))
           (gevents/listen @dom-node- goog.events.EventType.MOUSEDOWN el-mousedown)
           (when data
             (reset! cur-data* data)
             (reset! fuse-inst* (new Fuse
                                     (clj->js fuse-list)
                                     (clj->js (util/->camelCaseString (merge {:keys [:title :description :tags]
                                                                              :include-score true}
                                                                             fuse-config)))))
             (set-input-default-value search-default-value))))
       :component-did-update
       (fn [this]
         (let [{:keys [data fuse-list search-default-value]} (re/props this)]
           (when (and data @fuse-inst* (not= data @cur-data*))
             (.setCollection ^js @fuse-inst* (clj->js (or fuse-list
                                                          (build-fuse-list (util/flatten-tree {:children data})))))
             (reset! cur-data* data)
             (set-input-default-value search-default-value))))
       :component-will-unmount
       #(gevents/unlisten @dom-node- goog.events.EventType.MOUSEDOWN el-mousedown)
       :reagent-render
       (fn [{:keys [container-style label required? form-path form-name label-info size data extra-on-search-change
                    score-threshold placeholder auto-focus on-show on-hide floating? tree-size results-style
                    no-results-style no-results-text open? tree-style input-clearable? sort-by-score?
                    show-actions-toggle? support-arrow-key-selecting? blur-on-escape? search-debounce-time]
             :or {score-threshold 0.6
                  floating? true
                  tree-size :small}}]
         (let [on-show #(do
                          (when-not open? (reset! search-shown?* true))
                          (when on-show (on-show %)))
               on-hide #(when @search-shown?*
                          (when-not open? (reset! search-shown?* false))
                          (when on-hide (on-hide %)))
               loading? (= data :loading)
               theme @theme-
               match-scores (when-not loading?
                              (if (and val- (not-empty @val-))
                                (or (get @cached-match-scores- @val-)
                                    (let [exact-tag-matches (some->> (util/flatten-tree {:children data})
                                                                     (filter (fn [{:keys [exact-tags]}]
                                                                               (some #(= (str %) @val-)
                                                                                     (filter identity exact-tags))))
                                                                     (map :id)
                                                                     (map (fn [id]
                                                                            [id 0]))
                                                                     (into {}))
                                          scores (if (pos? (count exact-tag-matches))
                                                   exact-tag-matches
                                                   (some->> (when (and @fuse-inst* val-) (.search @fuse-inst* @val-))
                                                            (js->clj)
                                                            (util/->kebab-case-keywords)
                                                            (filter #(or false
                                                                         (< (:score %) score-threshold)))
                                                            (map (fn [{:keys [item score]}]
                                                                   [(:id item) score]))
                                                            (into {})))]
                                      (reset! cached-match-scores- (assoc @cached-match-scores- @val- scores))
                                      scores))
                                (let [scores (or (get @cached-match-scores- nil)
                                                 (some->> (util/flatten-tree {:children data})
                                                          (map :id)
                                                          (filter identity)
                                                          (map (fn [id]
                                                                 [id 0]))
                                                          (into {})))]
                                  (reset! cached-match-scores- (assoc @cached-match-scores- nil scores))
                                  scores)))
               build-results (fn [{:keys [ids-to-avoid ids-to-allow]}]
                               (let [res (if (or loading? (not data) (not val-))
                                           []
                                           (build-tree-results {:data data
                                                                :match-scores match-scores
                                                                :score-threshold score-threshold
                                                                :set-active set-active-result
                                                                :active-id @active-id*
                                                                :ids-to-avoid ids-to-avoid
                                                                :support-arrow-key-selecting? support-arrow-key-selecting?
                                                                :sort-by-score? sort-by-score?
                                                                :theme theme}))
                                     sel-res (filterv :id (selectable-tree-results {:children res}
                                                                                   {:ids-to-allow ids-to-allow}))]
                                 {:results res
                                  :selectable-results-by-id (->> sel-res
                                                                 (map-indexed (fn [idx {:keys [id] :as res}]
                                                                                [id (assoc res :idx idx)]))
                                                                 (into {}))
                                  :selectable-result-ids (mapv :id sel-res)}))
               {:keys [results selectable-results-by-id selectable-result-ids]} (build-results nil)
               clear-active-id #(do
                                  (reset! active-id* nil)
                                  (reset! active-id-method* nil))
               select-result-by-id #(let [{:keys [on-click checkable? radio? expandable? on-check disabled?]} (get selectable-results-by-id %)
                                          select-fn (or on-click (when checkable? on-check))
                                          active-id @active-id*
                                          active-item (when active-id (get selectable-results-by-id active-id))
                                          active-idx (:idx active-item)]
                                      (when select-fn
                                        (select-fn %)
                                        (when (and checkable?
                                                   radio?
                                                   (not expandable?)
                                                   active-idx)
                                          (let [res (build-results {:ids-to-avoid #{active-id}
                                                                    :ids-to-allow (:sibling-ids active-item)})
                                                next-idx (if (> active-idx (dec (count (:selectable-result-ids res))))
                                                           0
                                                           active-idx)]
                                            (reset! active-id-method* :arrow)
                                            (try
                                              (reset! active-id* (nth (:selectable-result-ids res) next-idx))
                                              (catch :default _))))))]
           [:div {:style (merge {:display :flex
                                 :flex-direction :column
                                 :position :relative}
                                container-style)}
            [input/input {:id input-id
                          :label label
                          :required? required?
                          :label-info label-info
                          :size size
                          :form-path form-path
                          :form-name form-name
                          :placeholder placeholder
                          :auto-focus auto-focus
                          :on-focus on-show
                          :on-blur on-hide
                          :form-change-debounce-time search-debounce-time
                          :clearable? input-clearable?
                          :extra-on-change (fn [v]
                                             (clear-active-id)
                                             (when extra-on-search-change (extra-on-search-change v)))
                          :on-key-down (fn [e]
                                         (cond
                                           (and support-arrow-key-selecting? (util/up-key-event? e))
                                           (let [cur-idx (when @active-id*
                                                           (:idx (get selectable-results-by-id @active-id*)))
                                                 next-idx (if (or (not cur-idx) (= cur-idx 0))
                                                            (dec (count selectable-result-ids))
                                                            (dec cur-idx))]
                                             (reset! active-id-method* :arrow)
                                             (try
                                               (reset! active-id* (nth selectable-result-ids next-idx))
                                               (catch :default _)))

                                           (and support-arrow-key-selecting? (util/down-key-event? e))
                                           (let [cur-idx (when @active-id*
                                                           (:idx (get selectable-results-by-id @active-id*)))
                                                 next-idx (if (or (not cur-idx)
                                                                  (= cur-idx (dec (count selectable-result-ids))))
                                                            0
                                                            (inc cur-idx))]
                                             (reset! active-id-method* :arrow)
                                             (try
                                               (reset! active-id*
                                                       (nth selectable-result-ids next-idx))
                                               (catch :default _)))

                                           (and support-arrow-key-selecting? (util/enter-key-event? e))
                                           (when @active-id* (select-result-by-id @active-id*))

                                           (and blur-on-escape? (util/escape-key-event? e))
                                           (when-let [input-el (.querySelector @dom-node- "input")]
                                             (.blur input-el))))}]
            (when (or open? @search-shown?*)
              (into [theme/theme
                     :div {:style (cond-> {:background-color (style/color :white :base)
                                           :box-shadow style/hover-box-shadow
                                           :display :flex
                                           :flex-direction :column
                                           :overflow :auto
                                           :margin-top 5
                                           :max-height 475
                                           :width "100%"}
                                          floating? (assoc :position :absolute
                                                           :top (if (= size :large) 41 38)
                                                           :left 0
                                                           :width "100%")
                                          results-style (merge results-style))
                           :theme (style/dark-style {:background-color (style/dark-color :surface :base)})
                           :on-mouse-out #(when (= @active-id-method* :hover) (clear-active-id))}]
                    (cond
                      loading? [[:div {:style {:padding 5}}
                                 [loading-bar {:height 9 :width "40%"}]
                                 [loading-bar {:height 9 :width "30%" :style {:margin "5px 0 0 10px"}}]
                                 [loading-bar {:height 9 :width "40%" :style {:margin "5px 0 0 10px"}}]]]
                      (= (count results) 0) [[no-results {:style no-results-style}
                                              (str (or no-results-text "No results found")
                                                   (when (and val- (not (s/blank? @val-)))
                                                     (str " for \""
                                                          @val-
                                                          "\"")))]]
                      :else [(if (= tree-size :small)
                               [tree/mini-tree {:style (merge {:margin 5}
                                                              tree-style)
                                                :children results}]
                               [tree/tree {:style (merge {:margin 5}
                                                         tree-style)
                                           :size tree-size
                                           :show-actions-toggle? show-actions-toggle?
                                           :children results}])])))]))})))
